import { useState, type ReactNode } from 'react';
import { Check, Copy, ExternalLink } from 'lucide-react';

/**
 * Placeholder shown in a write-only secret field when a key IS already stored, so the admin
 * can see it's set without ever revealing it. Focusing the field clears the mask to type a new
 * key; leaving it untouched (still === MASKED) means "keep the stored key unchanged".
 *
 * Shared by the Site-Integrations cards that put it in the field (Apify / Figma / Canva) so the
 * mask — and therefore the "did the admin type a new secret?" test — can never drift between them.
 * Social Media Integrations does not: its secret field always starts empty, a blank one keeps the
 * saved secret, and its own SECRET_MASK is only ever a placeholder or text.
 */
export const MASKED = '•••••••••';

/** Compact set / not-set indicator for a write-only secret (never shows the key). */
export function KeyStatus({ set }: { set: boolean }) {
  return set ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-success-tint dark:bg-success-dark-tint px-2 py-0.5 text-xs font-medium text-success dark:text-success-dark">
      <Check className="h-3.5 w-3.5" aria-hidden /> Key set
    </span>
  ) : (
    <span className="inline-flex items-center rounded-full bg-chip px-2 py-0.5 text-xs font-medium text-ink-muted">
      No key set
    </span>
  );
}

/** "Where do I get this?" style external link for a set-up panel header. */
export function SetupLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-link hover:text-action-hover"
    >
      {children} <ExternalLink className="h-3.5 w-3.5" aria-hidden />
    </a>
  );
}

/**
 * Numbered set-up instructions rendered INSIDE an integration card. Deliberately the same
 * numbered-step look as the social connect dialog's "What happens next" (tinted step chips), so
 * provider setup reads identically wherever it appears. The optional `action` holds a
 * "Where do I get this?" link.
 */
export function SetupSteps({
  title,
  action,
  steps,
}: {
  title: string;
  action?: ReactNode;
  steps: ReactNode[];
}) {
  return (
    <div className="rounded-xl border border-line-soft bg-page/60 px-4 py-3.5">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-semibold text-ink">{title}</p>
        {action}
      </div>
      <ol className="mt-3 space-y-2.5">
        {steps.map((step, i) => (
          <li key={i} className="flex items-start gap-3 text-sm text-ink-strong">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-tint text-xs font-bold text-tint-fg">
              {i + 1}
            </span>
            <div className="min-w-0 flex-1">{step}</div>
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * A copy-able URL the admin must register verbatim with the provider (redirect/callback URL):
 * a selectable code block + Copy button. Same purpose as the Social Media Integrations form's
 * redirect URL field, since a mistyped redirect URL is the single most common cause of a failed
 * OAuth round-trip.
 */
export function CopyableUrl({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — the code block is selectable as a fallback */
    }
  };

  return (
    <div className="mt-1.5 flex items-center gap-2">
      <code className="min-w-0 flex-1 truncate rounded bg-card px-2 py-1.5 text-xs text-ink-strong ring-1 ring-line">
        {value}
      </code>
      <button
        type="button"
        onClick={() => void copy()}
        className="focus-ring inline-flex shrink-0 items-center gap-1 rounded-lg border border-line-input bg-card px-2.5 py-1.5 text-xs font-medium text-ink-strong hover:bg-hover"
      >
        {copied ? (
          <>
            <Check className="h-3.5 w-3.5 text-success dark:text-success-dark" aria-hidden /> Copied
          </>
        ) : (
          <>
            <Copy className="h-3.5 w-3.5" aria-hidden /> Copy
          </>
        )}
      </button>
    </div>
  );
}
